import { formatPrice, type Channel, type Listing, type ProductRow } from '../lib/api';
import type { DraftEdit } from './ProductTable';

/**
 * One channel's price for one product, editable in place.
 *
 * Used twice per product: once on the master row, and once on that channel's
 * own row in the expanded detail, where it lands in the same column. Both need
 * identical geometry — the fixed-width percentage strip is what holds a column
 * on one right edge — so they share this rather than approximating each other.
 */

/**
 * How far a marketplace price sits above the website's, as a percentage.
 *
 * Returns null when there is nothing honest to say: no website price to measure
 * against, no marketplace price, or a website price of zero, where a percentage
 * is undefined rather than infinite.
 *
 * Only ever shown on a marketplace column. Against a storefront's own column it
 * would be comparing a price to itself, and the primary storefront would read
 * +0.0% on every row.
 */
export function websiteGap(
  priceMinor: number | null,
  referencePriceMinor: number | null,
): { label: string; percent: number } | null {
  if (priceMinor === null || referencePriceMinor === null || referencePriceMinor === 0) return null;

  const percent = ((priceMinor - referencePriceMinor) / referencePriceMinor) * 100;

  // Magnitude rounded, then the sign put back. Math.round breaks ties towards
  // positive infinity, so Math.round(-0.5) is -0 while Math.round(0.5) is 1 —
  // which rounded +0.05% up to "+0.1%" and −0.05% away to "same". A tool for
  // spotting a marketplace priced below the website must not round the
  // below-website direction into silence.
  const rounded = (Math.sign(percent) * Math.round(Math.abs(percent) * 10)) / 10;

  // Tested after rounding, so a price 0.02% off reads "same" rather than
  // "+0.0%" — which shows a difference and denies it in the same breath.
  if (rounded === 0) return { label: 'same', percent: 0 };

  // No decimal past 100%. It is noise at that scale, and the strip has a fixed
  // width — a mislink between a $12 item and a $1,200 one produces a four digit
  // percentage, and that is exactly when the figure needs to stay readable.
  const magnitude = Math.abs(rounded);
  const digits = magnitude >= 100 ? 0 : 1;

  return {
    label: `${rounded > 0 ? '+' : '−'}${magnitude.toFixed(digits)}%`,
    percent: rounded,
  };
}

interface PriceCellProps {
  channel: Channel;
  product: ProductRow;
  /** Which storefront referencePriceMinor came from, for the tooltip. */
  referenceName: string;
  canEdit: boolean;
  /**
   * Identifies this cell in the table-wide "which cell is being edited" state.
   * Passed in rather than derived, because the master row and the channel row
   * show the same product and channel and must still edit independently —
   * deriving it would put both into edit mode on one click.
   */
  cellKey: string;
  editingKey: string | null;
  draft: string;
  setDraft: (value: string) => void;
  onBeginEdit: (listing: Listing, cellKey: string) => void;
  onCommit: (listing: Listing, productTitle: string) => void;
  /** Ctrl/Cmd+Enter — commits this edit exactly as onCommit does, then immediately queues every draft this product has. */
  onCommitAndQueue: (listing: Listing, product: ProductRow) => void;
  onCancel: () => void;
  /** The local, unqueued draft for a listing's field, if any — see ProductTable's DraftEdit. */
  draftFor: (listingId: string, field: 'price' | 'title' | 'sku') => DraftEdit | undefined;
}

export function PriceCell({
  channel, product, referenceName, canEdit, cellKey, editingKey, draft, setDraft, onBeginEdit, onCommit,
  onCommitAndQueue, onCancel, draftFor,
}: PriceCellProps) {
  const listing = product.listings.find((l) => l.channelId === channel.id);
  const isSite = channel.type === 'drupal_uc' || channel.type === 'drupal_commerce';

  // An absent listing is not the same as a zero price, and must not look like one.
  if (!listing) {
    return (
      <td className="price-cell empty" title={`Not listed on ${channel.name}`}>
        —
      </td>
    );
  }

  const isEditing = editingKey === cellKey;
  // A blanket block wins; otherwise the price's own reason answers for it — an
  // Etsy listing pricing per variation, say.
  const blocked = listing.writeBlockedReason ?? listing.priceWriteBlockedReason;
  const staged = listing.pendingChanges.find((c) => c.field === 'price');
  // A local edit committed but not yet queued — hidden once `staged` exists,
  // since a change already on the server always outranks an unsent draft.
  const localDraft = staged ? undefined : draftFor(listing.id, 'price');
  // A write that's already left the queue — pushed, but not yet confirmed
  // live, or confirmed and then taken back. Shown only once `staged` is
  // gone, so a cell never carries two "→" notes at once.
  const unsettled = staged ? undefined : listing.unsettledWrites.find((w) => w.field === 'price');
  // Never on a storefront's own column: the primary one would read "same" on
  // every row, and the other would be measured against its sibling rather than
  // against itself, which is a comparison nobody asked for.
  const gap = isSite ? null : websiteGap(listing.priceMinor, product.referencePriceMinor);
  const siteName = referenceName;

  if (isEditing) {
    return (
      <td className="price-cell editing">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommit(listing, product.title)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onCommitAndQueue(listing, product);
            else if (e.key === 'Enter') onCommit(listing, product.title);
            else if (e.key === 'Escape') onCancel();
          }}
        />
      </td>
    );
  }

  return (
    <td
      className={`price-cell${listing.priceMismatch ? ' mismatch' : ''}${staged ? ' staged' : ''}${unsettled ? ' unsettled' : ''}${localDraft ? ' has-draft' : ''}${
        // Drives the resting pencil in the cell's gutter. Kept off a blocked or
        // read-only cell, so the mark never promises an edit that would be
        // refused.
        canEdit && !blocked ? ' editable' : ''
      }`}
      title={
        blocked
          ? `Not editable: ${blocked}`
          : listing.priceMismatch
            ? product.marketplacePriceMinor !== null
              ? `Differs from the other marketplaces, which charge ${formatPrice(product.marketplacePriceMinor, listing.currency)}`
              : 'The marketplaces disagree on this price with no majority'
            : undefined
      }
    >
      <span
        className={`price-value${blocked || !canEdit ? ' readonly' : ''}`}
        onClick={() => onBeginEdit(listing, cellKey)}
      >
        {formatPrice(listing.priceMinor, listing.currency)}
      </span>
      {/* What this marketplace charges over the website. Marketplace fees mean
          the normal shape is a few percent above, so sitting below is the
          notable case and is marked — undercutting your own storefront is
          usually a mistake rather than a decision.

          Rendered on every marketplace cell even when there is no percentage to
          show, because the strip is fixed width and holding it open is what
          keeps the prices above and below on one right edge. A row whose product
          has no website price would otherwise pull its whole column out of
          line. */}
      {!isSite && (
        <span
          className={`website-gap${gap && gap.percent < 0 ? ' below' : ''}`}
          title={
            gap === null
              ? undefined
              : gap.percent === 0
                ? `Same as the ${siteName} price`
                : `${Math.abs(gap.percent).toFixed(1)}% ${gap.percent > 0 ? 'above' : 'below'} the ${siteName} price of ${formatPrice(product.referencePriceMinor, listing.currency)}`
          }
        >
          {gap ? `(${gap.label})` : ''}
        </span>
      )}
      {/* Which channel is withholding the listing — otherwise the Inactive tab
          tells you a product is inactive somewhere without saying where. */}
      {listing.status === 'inactive' && (
        <span className="badge inactive" title={`Not currently shown on ${channel.name}`}>
          inactive
        </span>
      )}
      {/* The channel still charges the old price above until the queue is
          synced — this is what's waiting to replace it, not what's live now. */}
      {staged && (
        <div
          className={`staged-note${
            staged.status === 'conflict' || staged.status === 'needs_confirmation'
              ? ' conflict'
              : staged.status === 'failed'
                ? ' failed'
                : ''
          }`}
          title={
            staged.status === 'conflict'
              ? "Staged, but the channel's price has changed since — see the Pending tab"
              : staged.status === 'needs_confirmation'
                ? 'Staged, but this price change is larger than usual and needs confirming — see the Pending tab'
                : staged.status === 'failed'
                  ? 'Staged, but the last push failed — see the Pending tab'
                  : 'Staged — sync the queue to publish it'
          }
        >
          → {formatPrice(Number(staged.newValue), listing.currency)}
        </div>
      )}
      {/* The queue row is gone once a write is pushed — this is what keeps
          the cell honest in the gap between "queued" and "confirmed live",
          and after, if the channel takes the change back. */}
      {unsettled && (
        <div className={`staged-note unsettled-note ${unsettled.writeState}`} title={unsettledWriteTitle(unsettled)}>
          → {formatPrice(Number(unsettled.newValue), listing.currency)}
        </div>
      )}
      {/* Edited locally but not yet queued — distinct from `staged` above,
          which has already reached the server. "Queue Changes" (or
          Ctrl+Enter) is what turns this into a staged note. */}
      {localDraft && (
        <div className="draft-note" title="Edited but not yet queued — click Queue Changes to stage it">
          → {formatPrice(Number(localDraft.value), listing.currency)}
        </div>
      )}
    </td>
  );
}

function unsettledWriteTitle(write: Listing['unsettledWrites'][number]): string {
  if (write.message) return write.message;
  switch (write.writeState) {
    case 'in_flight':
      return 'Pushed just now — waiting on the channel to accept it';
    case 'awaiting_confirmation':
      return `Accepted, but not yet confirmed live — recheck ${write.checkAttempts + 1} of 4`;
    case 'reverted_by_channel':
      return 'Confirmed live, then the channel reverted it';
    case 'not_confirmed':
      return 'Pushed, but never confirmed live';
  }
}
